import type { UsageStanding } from '../services/usageApiService'

/**
 * How a team's billing standing is presented, in one place so the dashboard and the billing page
 * cannot drift into saying different things about the same state.
 *
 * Free is assigned by DocMorphia to special teams: Standard's page allowance, never charged, never blocked,
 * so its usage is informational. Standard, Enhanced and Ultimate each carry a monthly minimum with an
 * allowance of PAGES included in it, and keep going past the allowance at a published per-page rate. So the unit on
 * screen follows the plan, and the figure that matters for the tone is not the plan's name but whether
 * the allowance can actually be exceeded — `allowsOverage`.
 */

/** Warn from here on, so a capped team sees the limit coming rather than hitting it mid-render. */
const WARN_AT = 0.8

export type StandingTone = 'normal' | 'warning' | 'blocked'

/**
 * Which allowance applies, and what the team has used against it.
 *
 * A plan caps pages or documents, never both — so one lookup answers "what are we counting" for every
 * caller, and no screen has to guess.
 */
export interface Allowance {
  unit: 'page' | 'document'
  included: number
  used: number
}

export function allowanceFor(standing: UsageStanding | undefined): Allowance | null {
  if (!standing) return null
  if (standing.includedPages != null) {
    return { unit: 'page', included: standing.includedPages, used: standing.pagesThisPeriod ?? 0 }
  }
  if (standing.includedDocuments != null) {
    return { unit: 'document', included: standing.includedDocuments, used: standing.usedThisPeriod }
  }
  return null
}

/** The team's overage settings (V084) as the API reports them. */
export interface OverageSettingsView {
  allowOverage?: boolean
  overageLimitDollars?: number | null
}

/** The limit every new account starts with (billing_account.overage_limit_dollars DEFAULT 25). */
export const DEFAULT_OVERAGE_LIMIT_DOLLARS = 25
export const MAX_OVERAGE_LIMIT_DOLLARS = 1_000_000

/**
 * Whole pages past the allowance a limit pays for at the per-page rate — the API's OverageSettings.ExtraPages, rounded
 * down so those pages never bill above the limit. Null when a page past the allowance costs nothing.
 */
export function extraPagesFor(limitDollars: number, perPageCents: number | null | undefined): number | null {
  if (perPageCents == null || perPageCents <= 0) return null
  return Math.floor((limitDollars * 100) / perPageCents + 1e-9)
}

/**
 * The one-line overage summary shown in the plan card and the current bill, so the two cannot disagree:
 * "Overage: billed at $0.0105/page, up to $25 a month", "Overage: billed at $0.0105/page" (no limit) or
 * "Overage: off — renders stop at 5,000 pages". Null when the settings do not apply.
 */
export function overageSummary(
  settings: OverageSettingsView, applies: boolean | undefined,
  perPageCents: number | null | undefined, includedPages: number | null | undefined,
): string | null {
  if (!applies || includedPages == null) return null
  if (settings.allowOverage === false) return `Overage: off — renders stop at ${includedPages.toLocaleString()} pages`
  const rate = perPageCents != null ? `Overage: billed at ${formatCents(perPageCents)}/page` : 'Overage: billed at the plan rate'
  return settings.overageLimitDollars != null
    ? `${rate}, up to $${settings.overageLimitDollars.toLocaleString()} a month`
    : rate
}

/** Pluralised in one place, since every string below needs it. */
function units(count: number, unit: 'page' | 'document'): string {
  return `${count.toLocaleString()} ${unit}${count === 1 ? '' : 's'}`
}

/**
 * Cents to a displayable price.
 *
 * Two decimal places for an ordinary amount, and full precision for a per-page price. A per-page price
 * is fractional — Enhanced's is $0.0089 — and printing it as $0.01 would contradict the invoice, which
 * is exactly the discrepancy a customer recomputing their own bill would find. So the choice is made by
 * whether the value is already exact at two places, not by its size.
 */
export function formatCents(cents: number): string {
  const dollars = cents / 100
  const exactAtCents = Math.abs(cents - Math.round(cents)) < 1e-9

  return exactAtCents
    ? `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${dollars.toFixed(11).replace(/0+$/, '')}`
}

export function standingTone(standing: UsageStanding | undefined): StandingTone {
  if (!standing) return 'normal'
  if (standing.isSuspended) return 'blocked'
  // The plan is unchanged and the team keeps everything it had — but it cannot do chargeable work
  // until a card is back on file.
  if (standing.requiresPaymentMethod && !standing.hasPaymentMethod) return 'blocked'
  // Nothing to be near: going over costs more rather than stopping.
  if (standing.allowsOverage) return 'normal'

  const allowance = allowanceFor(standing)
  if (!allowance) return 'normal'
  if (allowance.used >= allowance.included) return 'blocked'
  return allowance.used / allowance.included >= WARN_AT ? 'warning' : 'normal'
}

/**
 * The headline figure: usage this month against the allowance it counts against, in whichever unit the
 * plan meters. A plan with no allowance shows the bare count, because a count out of nothing is not a
 * fraction.
 */
/** What standingValue counts — pages on a page allowance, documents on a document one, renders otherwise. */
export function standingUnit(standing: UsageStanding | undefined): 'Pages' | 'Documents' | 'Renders' {
  const unit = allowanceFor(standing)?.unit
  return unit === 'page' ? 'Pages' : unit === 'document' ? 'Documents' : 'Renders'
}

export function standingValue(standing: UsageStanding | undefined, fallbackRenders: number): string {
  const allowance = allowanceFor(standing)
  if (!allowance) return (standing?.usedThisPeriod ?? fallbackRenders).toLocaleString()
  return `${allowance.used.toLocaleString()} / ${allowance.included.toLocaleString()}`
}

/** The supporting line, or null when there is nothing worth saying. */
export function standingHint(standing: UsageStanding | undefined): string | null {
  if (!standing) return null
  if (standing.isSuspended) return 'Rendering suspended — payment could not be collected'
  if (standing.requiresPaymentMethod && !standing.hasPaymentMethod) {
    return 'Add a payment method to render documents and save templates'
  }

  const allowance = allowanceFor(standing)
  if (standing.planCode === 'free') return 'No charge — your plan is assigned by DocMorphia'
  if (!allowance) return 'Billed monthly for everything rendered'

  const left = Math.max(0, allowance.included - allowance.used)
  const rate = standing.perPageCents != null ? `${formatCents(standing.perPageCents)} per page` : 'the overage rate'

  if (standing.allowsOverage) {
    // Not a warning: going over is expected and priced, so saying "limit reached" would be alarming
    // and wrong. What the customer needs to know is that the rate changes, and to what.
    return left > 0
      ? `${units(left, allowance.unit)} included this month`
      : `Included ${allowance.unit}s used — further ${allowance.unit}s bill at ${rate}`
  }

  return left > 0
    ? `${units(left, allowance.unit)} left this month`
    : 'Free allowance used — add a payment method to continue'
}

/** Whether the team should be pointed at the billing page to add a card. */
export function shouldOfferBilling(standing: UsageStanding | undefined): boolean {
  return standing != null && !standing.hasPaymentMethod && standingTone(standing) !== 'normal'
}
